// src/features/rrhh/liquidacion/hooks/useLiquidacionData.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { listPayrollClosures, listMissingEmployees } from '../api/liquidacionApi';

function unwrapHttp(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data;
  return res;
}

function errorToMessage(err) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    'Error al cargar datos'
  );
}

/**
 * Hook para cargar y filtrar cierres con paginación del backend.
 *
 * El problema original: usar JSON.stringify(filters) en el dep array de useCallback
 * genera un nuevo string en cada render aunque los valores no cambiaron, lo que
 * dispara un bucle infinito cuando se agrega `filters` como dependencia.
 *
 * Solución: guardamos los filtros en un ref para que fetchClosures siempre acceda
 * al valor más reciente sin necesitar `filters` como dependencia del useCallback.
 * El useEffect observa los filtros primitivos individualmente para re-disparar la
 * fetch sólo cuando realmente cambia algún valor.
 */
export function usePayrollClosures(filters) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ref que siempre tiene los filtros actuales sin ser dependencia del callback
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  });

  const fetchClosures = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await listPayrollClosures(filtersRef.current);
      const unwrapped = unwrapHttp(res);

      let items = unwrapped?.data || [];
      let total = unwrapped?.meta?.totalItems || 0;

      if (Array.isArray(unwrapped)) {
        items = unwrapped;
        total = items.length;
      }

      setData({ items, total });
    } catch (err) {
      setData({ items: [], total: 0 });
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, []); // ← sin dependencias: fetchClosures es estable durante toda la vida del componente

  // Re-dispara la fetch cada vez que cambia algún filtro primitivo
  useEffect(() => {
    fetchClosures();
  }, [
    fetchClosures,
    filters.status,
    filters.name,
    filters.periodStart,
    filters.periodEnd,
    filters.page,
    filters.limit,
  ]);

  return { closures: data.items, totalItems: data.total, loading, error, refetch: fetchClosures };
}

/**
 * Hook para cargar empleados faltantes
 */
export function useMissingEmployees() {
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await listMissingEmployees();
      const data = unwrapHttp(res);
      const list = Array.isArray(data) ? data : data?.data || [];
      setMissing(list);
    } catch (err) {
      setMissing([]);
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { missing, loading, error, refetch: fetch };
}